import UArray from "./UArray.mts";
import UFunction from "./UFunction.mts";
import UObject from "./UObject.mts";
import UType from "./UType.mts";

type Table<Key extends UObject.Key = UObject.Key, Value = any> = Record<
  Key,
  Value
>[];

namespace Table {
  export type Index<
    T extends Table,
    Layers extends readonly Index.Layer<T>[],
    PrimaryKey extends keyof T[number],
  > = Layers extends readonly [infer Layer, ...infer Rest]
    ? {
        [Key in UType.Narrow<
          Layer extends keyof T[number]
            ? T[number][Layer]
            : ReturnType<UType.Narrow<Layer, UFunction.Any>>,
          UObject.Key
        >]: Rest extends []
          ? T[number][PrimaryKey]
          : Index<T, UType.Narrow<Rest, readonly Index.Layer<T>[]>, PrimaryKey>;
      }
    : never;
  export namespace Index {
    export type IndexableKey<T extends Table> = UObject.Key.FilterByEntry<
      T[number],
      [any, UObject.Key]
    >;
    export type Layer<T extends Table> =
      | IndexableKey<T>
      | ((item: T[number]) => UObject.Key);
  }

  export function createIndex<
    T extends Table,
    const Layers extends readonly Table.Index.Layer<T>[],
    PrimaryKey extends keyof T[number],
  >(
    table: T,
    layers: Layers,
    primaryKey: PrimaryKey,
  ): Table.Index<T, Layers, PrimaryKey> {
    const res = {};

    for (const item of table) {
      UObject.mut.setByPath.createParents(
        res as any,
        layers.map((layer) =>
          typeof layer === "function"
            ? layer(item as any)
            : (item as any)[layer],
        ),
        (item as any)[primaryKey],
      );
    }

    return res as any;
  }

  createIndex.primary = <
    T extends Table,
    PrimaryKey extends Table.Index.IndexableKey<T>,
  >(
    table: T,
    primaryKey: PrimaryKey,
  ): Record<UType.Narrow<T[number][PrimaryKey], UObject.Key>, T[number]> =>
    Object.fromEntries(table.map((record) => [record[primaryKey], record]));

  export function sumColumns<
    const T extends Table,
    Column extends keyof T[number],
  >(table: T, columns: readonly Column[]): Record<Column, number> {
    return Object.fromEntries(
      columns.map((column) => [column, UArray.sum(table, column)]),
    );
  }
}

export default Table;
